import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// Merchandising fabric calculator. fabric_master is shared; the recipe carries the
// company. Setter (admin/management/procurement_officer) defines the per-company recipe;
// puncher (merchandiser) enters yarn prices → total via the shared cost engine.
const SETTERS = ['admin', 'management', 'procurement_officer'];
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const r2 = (v) => '₹' + (Math.round((num(v)) * 100) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const input = { padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const lab = { fontSize: 10, color: '#888', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase' };
const card = { background: '#fff', border: '1px solid #E8E7E7', borderRadius: 10, padding: 16 };

export default function MerchFabricCalc() {
  const { user } = useAuth();
  const isSetter = SETTERS.includes(user?.role);
  // Setters land on the recipe editor; merchandiser (puncher) lands on the calculator
  // and never sees the Recipes tab (also enforced server-side by the SETTER guard).
  const [tab, setTab] = useState(isSetter ? 'recipes' : 'calc');
  const [companies, setCompanies] = useState([]); const [canSwitch, setCanSwitch] = useState(false); const [company, setCompany] = useState('');
  const H = useCallback(() => ({ headers: { 'X-Company-Id': company } }), [company]);

  useEffect(() => {
    api.get('/merch/companies').then(r => { setCompanies(r.data.data || []); setCanSwitch(r.data.can_switch); setCompany(r.data.active || (r.data.data[0] && r.data.data[0].id) || ''); }).catch(() => {});
  }, []);
  const companyName = companies.find(c => c.id === company)?.name || '—';

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', fontFamily: 'inherit' }}>
      <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20, color: '#1a1a2e' }}>🧵 Fabric Cost Calculator</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>Company</span>
            <select value={company} disabled={!canSwitch} onChange={e => setCompany(e.target.value)} style={{ ...input, fontWeight: 700, color: '#3B3A8F', background: canSwitch ? '#EEEDF8' : '#f5f5f5' }}>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          <button onClick={() => setTab('calc')} style={tabBtn(tab === 'calc')}>🧮 Calculator</button>
          {isSetter && <button onClick={() => setTab('recipes')} style={tabBtn(tab === 'recipes')}>⚙️ Recipes (setter)</button>}
        </div>

        {tab === 'calc' && <Calculator H={H} companyName={companyName} />}
        {tab === 'recipes' && isSetter && <Recipes H={H} companyName={companyName} />}
      </div>
    </div>
  );
}
const tabBtn = (on) => ({ padding: '8px 16px', border: '1px solid ' + (on ? '#3B3A8F' : '#ddd'), background: on ? '#3B3A8F' : '#fff', color: on ? '#fff' : '#555', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700 });

// ── PUNCHER. Merchant sees ONLY the final fabric price; setters also see the breakdown.
//    Compute is server-side — the recipe (route/costs/losses/margin) never reaches a merchant. ──
function Calculator({ H, companyName }) {
  const { user } = useAuth();
  const isSetter = SETTERS.includes(user?.role);
  const [fabrics, setFabrics] = useState([]); const [fid, setFid] = useState('');
  const [hasRecipe, setHasRecipe] = useState(false); const [comp, setComp] = useState([]); const [prices, setPrices] = useState([]);
  const [total, setTotal] = useState(null); const [breakdown, setBreakdown] = useState(null);
  const [saved, setSaved] = useState([]); const [msg, setMsg] = useState('');

  const loadFabrics = useCallback(() => api.get('/merch/fabrics?withRecipe=1', H()).then(r => setFabrics(r.data.data || [])).catch(() => {}), [H]);
  const loadSaved = useCallback(() => api.get('/merch/calcs', H()).then(r => setSaved(r.data.data || [])).catch(() => {}), [H]);
  useEffect(() => { loadFabrics(); loadSaved(); setFid(''); setHasRecipe(false); setComp([]); setTotal(null); setBreakdown(null); }, [loadFabrics, loadSaved]);

  const pick = (id) => {
    setFid(id); setMsg(''); setTotal(null); setBreakdown(null);
    if (!id) { setHasRecipe(false); setComp([]); setPrices([]); return; }
    api.get('/merch/recipe/' + id, H()).then(r => {
      setHasRecipe(!!r.data.data.recipe); const c = r.data.data.composition || [];   // merchant: yarn names only (no %s/costs)
      setComp(c); setPrices(c.map(x => x.standard_rate ?? ''));   // prefill today's price from yarn-master rate (editable)
    }).catch(() => {});
  };
  // Compute on the SERVER (debounced). Merchant response = { total_cost } only; setter also gets breakdown.
  useEffect(() => {
    if (!fid || !comp.length) return;
    const t = setTimeout(() => {
      api.post('/merch/compute', { fabric_master_id: fid, prices: prices.map(num) }, H())
        .then(r => { setTotal(r.data.data.total_cost); setBreakdown(r.data.data.breakdown || null); })
        .catch(() => { setTotal(null); setBreakdown(null); });
    }, 300);
    return () => clearTimeout(t);
  }, [fid, prices, comp.length, H]);

  const save = () => {
    api.post('/merch/calcs', { fabric_master_id: fid, prices: prices.map(num) }, H())
      .then(r => { setMsg(`Saved — ₹${r.data.data.total_cost}/kg.`); loadSaved(); }).catch(e => setMsg(e.response?.data?.error || 'Save failed'));
  };
  const td = { padding: '7px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5' };

  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ ...card, flex: '1 1 460px', minWidth: 420 }}>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>Pick a fabric with a current <b>{companyName}</b> recipe, then enter today's yarn ₹/kg.</div>
        <select value={fid} onChange={e => pick(e.target.value)} style={{ ...input, width: '100%', marginBottom: 12 }}>
          <option value="">— pick fabric —</option>
          {fabrics.map(f => <option key={f.id} value={f.id}>{f.fabric_name} · {f.composition || ''} (recipe v{f.recipe_version})</option>)}
        </select>
        {fabrics.length === 0 && <div style={{ color: '#aaa', fontSize: 12 }}>No fabrics have a {companyName} recipe yet — ask a recipe-setter.</div>}

        {hasRecipe && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#555', margin: '6px 0' }}>YARN PRICES (today ₹/kg)</div>
            {comp.map((y, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                <span style={{ flex: 1, fontSize: 13 }}>{y.yarn_name}{isSetter && y.percentage != null ? <span style={{ color: '#999' }}> · {y.percentage}%</span> : ''}</span>
                <input type="number" step="0.01" value={prices[i]} onChange={e => setPrices(p => p.map((x, j) => j === i ? e.target.value : x))} style={{ ...input, width: 110 }} placeholder="₹/kg" />
              </div>
            ))}
            {comp.length === 0 && <div style={{ color: '#A32D2D', fontSize: 12 }}>This fabric has no yarn composition set — ask a setter to add it.</div>}

            {/* The ONE number a merchant sees */}
            <div style={{ marginTop: 14, padding: '14px 16px', background: '#EEEDF8', border: '1px solid #C9C7EC', borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#3B3A8F' }}>Fabric cost</span>
              <span style={{ fontSize: 26, fontWeight: 800, color: '#3B3A8F' }}>{total != null ? r2(total) + ' /kg' : '—'}</span>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12 }}>
              <button onClick={save} disabled={total == null} style={{ ...input, background: total == null ? '#aaa' : '#3B3A8F', color: '#fff', border: 'none', cursor: total == null ? 'default' : 'pointer', fontWeight: 700 }}>💾 Save calc</button>
              {msg && <span style={{ fontSize: 12, color: '#166534' }}>{msg}</span>}
            </div>
          </>
        )}
      </div>

      {/* Cost build-up — SETTERS ONLY. A merchant's browser never receives these numbers. */}
      {isSetter && breakdown && (
        <div style={{ flex: '1 1 300px', minWidth: 300, border: '1px solid #E8E7E7', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ background: '#1a1a2e', color: '#fff', padding: '10px 14px', fontWeight: 700, fontSize: 13 }}>Cost build-up ({companyName} recipe) · admin only</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
            {breakdown.map((l, i) => (
              <tr key={i} style={{ background: l.total ? '#EEEDF8' : l.bold ? '#f7f7fb' : 'transparent' }}>
                <td style={{ ...td, fontWeight: l.bold ? 800 : 400, fontSize: l.total ? 14 : 12 }}>{l.label}{l.note ? <span style={{ color: '#aaa', fontWeight: 400 }}> · {l.note}</span> : ''}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: l.bold ? 800 : 500, fontSize: l.total ? 16 : 12, color: l.total ? '#3B3A8F' : '#222' }}>{r2(l.amount)}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
      )}

      <div style={{ ...card, flex: '1 1 100%' }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Saved calcs ({companyName})</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ background: '#F7F6F3' }}>{['Date', 'Fabric', 'Recipe v', 'Total ₹/kg'].map(h => <th key={h} style={{ ...td, fontWeight: 700, textAlign: h === 'Total ₹/kg' ? 'right' : 'left' }}>{h}</th>)}</tr></thead>
          <tbody>
            {saved.length === 0 ? <tr><td colSpan={4} style={{ padding: 16, textAlign: 'center', color: '#aaa' }}>No saved calcs yet</td></tr>
              : saved.map(s => <tr key={s.id}><td style={td}>{String(s.calc_date).slice(0, 10)}</td><td style={td}>{s.fabric_name}</td><td style={td}>v{s.recipe_version}</td><td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{r2(s.total_cost)}</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── SETTER ───────────────────────────────────────────────────────────────────
function Recipes({ H, companyName }) {
  const [fabrics, setFabrics] = useState([]); const [fid, setFid] = useState('');
  const [route, setRoute] = useState([{ name: 'Knitting', cost: '' }]);
  const [losses, setLosses] = useState({ knitting_process_loss_pct: '', process_loss_pct: '', overheads: '', margin_pct: '' });
  const [comp, setComp] = useState([]); const [yarnMaster, setYarnMaster] = useState([]);
  const [msg, setMsg] = useState(''); const [ver, setVer] = useState(null);

  const loadFabrics = useCallback(() => api.get('/merch/fabrics', H()).then(r => setFabrics(r.data.data || [])).catch(() => {}), [H]);
  useEffect(() => { loadFabrics(); api.get('/oe/yarn-master').then(r => setYarnMaster(r.data.data || [])).catch(() => {}); }, [loadFabrics]);

  const pick = (id) => {
    setFid(id); setMsg('');
    if (!id) return;
    api.get('/merch/recipe/' + id, H()).then(r => {
      const rec = r.data.data.recipe;
      setVer(rec?.version || null);
      setRoute(rec?.process_lines?.length ? rec.process_lines.map(l => ({ name: l.name, cost: l.cost, key: l.key })) : [{ name: 'Knitting', cost: '' }]);
      setLosses({ knitting_process_loss_pct: rec?.knitting_process_loss_pct ?? '', process_loss_pct: rec?.process_loss_pct ?? '', overheads: rec?.overheads ?? '', margin_pct: rec?.margin_pct ?? '' });
      setComp((r.data.data.composition || []).map(c => ({ yarn_master_id: c.yarn_master_id || '', yarn_name: c.yarn_name, percentage: c.percentage })));
    }).catch(() => {});
  };
  const pctSum = comp.reduce((a, y) => a + num(y.percentage), 0);

  const saveRecipe = () => {
    const process_lines = route.filter(r => r.name || r.cost).map((r, i) => ({ key: i === 0 ? 'knitting' : 'process', name: r.name, cost: num(r.cost) }));
    api.post('/merch/recipe/' + fid, { process_lines, ...Object.fromEntries(Object.entries(losses).map(([k, v]) => [k, num(v)])) }, H())
      .then(r => { setVer(r.data.data.version); setMsg(`Recipe saved as v${r.data.data.version} for ${companyName}.`); loadFabrics(); }).catch(e => setMsg(e.response?.data?.error || 'Save failed'));
  };
  const saveComp = () => {
    api.post('/oe/fabric-yarn/' + fid, { yarns: comp.map(y => ({ yarn_master_id: y.yarn_master_id || null, yarn_name: y.yarn_name, percentage: num(y.percentage) })) })
      .then(() => setMsg('Composition saved.')).catch(e => setMsg(e.response?.data?.error || 'Composition save failed (Σ must be 100%)'));
  };
  const pickYarn = (i, id) => { const m = yarnMaster.find(y => String(y.id) === String(id)); setComp(c => c.map((y, j) => j === i ? { ...y, yarn_master_id: id, yarn_name: m ? m.yarn_name : y.yarn_name } : y)); };

  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ ...card, flex: '1 1 480px', minWidth: 440 }}>
        <div style={{ background: '#FFF7E6', border: '1px solid #F2D9A0', borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>
          ⚙️ Editing the <b style={{ color: '#854F0B' }}>{companyName}</b> recipe. RSR and Deroi keep separate recipes for the same fabric.{ver ? ` Current: v${ver}.` : ' No recipe yet.'}
        </div>
        <select value={fid} onChange={e => pick(e.target.value)} style={{ ...input, width: '100%', marginBottom: 12 }}>
          <option value="">— pick fabric —</option>
          {fabrics.map(f => <option key={f.id} value={f.id}>{f.fabric_name} · {f.composition || ''}{f.recipe_id ? ` (has v${f.recipe_version})` : ' (no recipe)'}</option>)}
        </select>

        {fid && <>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#555', marginBottom: 6 }}>PROCESS ROUTE (₹/kg) — row 1 is Knitting</div>
          {route.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <span style={{ width: 14, fontSize: 11, color: '#aaa' }}>{i + 1}</span>
              <input value={r.name} onChange={e => setRoute(x => x.map((y, j) => j === i ? { ...y, name: e.target.value } : y))} placeholder="process" style={{ ...input, flex: 1 }} />
              <input type="number" step="0.0001" value={r.cost} onChange={e => setRoute(x => x.map((y, j) => j === i ? { ...y, cost: e.target.value } : y))} placeholder="₹/kg" style={{ ...input, width: 100 }} />
              <button onClick={() => setRoute(x => x.filter((_, j) => j !== i))} disabled={i === 0} style={{ border: 'none', background: 'none', color: '#A32D2D', cursor: 'pointer', opacity: i === 0 ? 0.3 : 1 }}>✕</button>
            </div>
          ))}
          <button onClick={() => setRoute(x => [...x, { name: '', cost: '' }])} style={{ ...input, background: '#EEEDF8', color: '#3B3A8F', border: '1px dashed #3B3A8F', cursor: 'pointer', fontWeight: 700, marginBottom: 12 }}>+ Add process</button>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {[['knitting_process_loss_pct', 'Knitting loss %'], ['process_loss_pct', 'Process loss %'], ['overheads', 'Overheads ₹/kg'], ['margin_pct', 'Margin %']].map(([k, l]) =>
              <div key={k}><label style={lab}>{l}</label><input type="number" step="0.0001" value={losses[k]} onChange={e => setLosses(s => ({ ...s, [k]: e.target.value }))} style={{ ...input, width: 110 }} /></div>)}
          </div>
          <button onClick={saveRecipe} style={{ ...input, marginTop: 14, background: '#3B3A8F', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, padding: '8px 18px' }}>💾 Save recipe (new version)</button>
          {msg && <div style={{ fontSize: 12, color: '#166534', marginTop: 8 }}>{msg}</div>}
        </>}
      </div>

      {fid && (
        <div style={{ ...card, flex: '1 1 360px', minWidth: 340 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#555', marginBottom: 6 }}>YARN COMPOSITION %s{' '}
            <span style={{ padding: '1px 7px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: Math.abs(pctSum - 100) > 0.01 ? '#FCEBEB' : '#EAF7EE', color: Math.abs(pctSum - 100) > 0.01 ? '#A32D2D' : '#166534' }}>Σ {pctSum}%</span></div>
          {comp.map((y, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <select value={y.yarn_master_id || ''} onChange={e => pickYarn(i, e.target.value)} style={{ ...input, width: 130 }}><option value="">— yarn —</option>{yarnMaster.map(m => <option key={m.id} value={m.id}>{m.yarn_name}</option>)}</select>
              <input value={y.yarn_name} onChange={e => setComp(c => c.map((x, j) => j === i ? { ...x, yarn_name: e.target.value } : x))} placeholder="name" style={{ ...input, flex: 1 }} />
              <input type="number" step="0.01" value={y.percentage} onChange={e => setComp(c => c.map((x, j) => j === i ? { ...x, percentage: e.target.value } : x))} placeholder="%" style={{ ...input, width: 64 }} />
              <button onClick={() => setComp(c => c.filter((_, j) => j !== i))} style={{ border: 'none', background: 'none', color: '#A32D2D', cursor: 'pointer' }}>✕</button>
            </div>
          ))}
          <button onClick={() => setComp(c => [...c, { yarn_master_id: '', yarn_name: '', percentage: '' }])} style={{ ...input, background: '#EEEDF8', color: '#3B3A8F', border: '1px dashed #3B3A8F', cursor: 'pointer', fontWeight: 700, marginBottom: 10 }}>+ Add yarn</button>
          <div><button onClick={saveComp} style={{ ...input, background: '#166534', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700 }}>💾 Save composition</button></div>
        </div>
      )}
    </div>
  );
}
